export type SchemaTier = 'normal' | 'advanced' | 'staff';

export interface SchemaProperty {
    type: string | string[];
    enum?: string[];
    format?: string;
    'x-access'?: string;
    /** Tier gate: 'advanced' hides the field unless the user turned on Advanced mode (`useBackofficeAuth().showAdvanced`). 'staff' is equivalent to `x-access: staff`. Defaults to 'normal'. */
    'x-tier'?: SchemaTier;
    'ui:widget'?: string;
    'ui:title'?: string;
    'ui:tooltip'?: string;
    'ui:description'?: string;
    'ui:group'?: string;
    'ui:subgroup'?: string;
    'ui:sectionTitle'?: string;
    'ui:sectionDescription'?: string;
    /** Lucide icon name for the grouped section card header (see SECTION_ICONS in ConfigSection). */
    'ui:sectionIcon'?: string;
    'ui:keyPlaceholder'?: string;
    'ui:addLabel'?: string;
    'ui:separatorBefore'?: boolean;
    'ui:separatorAfter'?: boolean;
    'ui:spacingBefore'?: boolean;
    'ui:keyHint'?: string;
    'ui:example'?: Record<string, unknown>;
    'ui:wizard'?: string;
    'ui:hidden'?: boolean;
    'ui:show-when'?: Record<string, unknown>;
    readOnly?: boolean;
    'ui:presets'?: Record<string, string>;
    'ui:enumDescriptions'?: Record<string, string>;
    'ui:enumLabels'?: Record<string, string>;
    /** Placeholder text for text-style widgets (e.g. name-generator). */
    'ui:placeholder'?: string;
    /** Per-key placeholder text for language-map entries, e.g. { en: "current default copy" }. */
    'ui:placeholderDefaults'?: Record<string, string>;
    /**
     * Deprecation notice rendered above the field. When set AND value is empty,
     * the field becomes read-only so new content cannot be added. Existing
     * non-empty values stay editable so staff can migrate them away.
     */
    'ui:deprecation-notice'?: string;
    properties?: Record<string, SchemaProperty>;
    additionalProperties?: SchemaProperty | boolean;
    items?: SchemaProperty;
    minimum?: number;
    maximum?: number;
    pattern?: string;
    maxLength?: number;
    minLength?: number;
    required?: string[];
    /** JSON Schema composition. Currently only `required` is collected from each subschema. */
    allOf?: SchemaProperty[];
    default?: unknown;
}

export interface ConfigSchema {
    $id: string;
    title: string;
    description: string;
    type: string;
    'x-access'?: string;
    'x-tier'?: SchemaTier;
    'x-feature'?: boolean;
    'x-manage-link'?: string;
    properties?: Record<string, SchemaProperty>;
    required?: string[];
    default?: Record<string, unknown>;
}

/** Resolve the effective tier for a schema node. `x-access: staff` overrides any `x-tier`. */
export function effectiveTier(node: { 'x-access'?: string; 'x-tier'?: SchemaTier }): SchemaTier {
    if (node['x-access'] === 'staff') return 'staff';
    const t = node['x-tier'];
    if (t === 'advanced' || t === 'staff') return t;
    return 'normal';
}

/** Whether a tier should render given the current user + advanced toggle. */
export function isTierVisible(tier: SchemaTier, isStaff: boolean, showAdvanced: boolean): boolean {
    if (tier === 'staff') return isStaff;
    if (tier === 'advanced') return isStaff || showAdvanced;
    return true;
}

/** Resolve the effective type from a schema property (handles nullable arrays like ["string", "null"]). */
export function resolveType(schema: SchemaProperty): string {
    if (Array.isArray(schema.type)) {
        return schema.type.find((t) => t !== 'null') ?? 'string';
    }
    return schema.type;
}

/** Format snake_case key to Title Case label. */
export function formatLabel(key: string): string {
    return key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/** Auto-discover all schema files via Vite glob import. Adding a new .schema.json is enough. */
const globalModules = import.meta.glob('../../../../schemas/configs/global/*.schema.json', {
    eager: true,
});
const websiteModules = import.meta.glob('../../../../schemas/configs/website/*.schema.json', {
    eager: true,
});
const adminModules = import.meta.glob('../../../../schemas/configs/admin/*.schema.json', {
    eager: true,
});

function extractSchemas(modules: Record<string, unknown>): ConfigSchema[] {
    return Object.values(modules).map((m) => (m as { default: ConfigSchema }).default);
}

/** Schema slugs that have their own dedicated page and should not appear in Settings. */
const HIDDEN_SCHEMAS = new Set(['agents']);

export const GLOBAL_SCHEMAS: ConfigSchema[] = extractSchemas(globalModules).filter(
    (s) => !HIDDEN_SCHEMAS.has(s.$id)
);
export const WEBSITE_SCHEMAS: ConfigSchema[] = extractSchemas(websiteModules);
export const ADMIN_SCHEMAS: ConfigSchema[] = extractSchemas(adminModules);
const ALL_SCHEMAS = [...GLOBAL_SCHEMAS, ...WEBSITE_SCHEMAS, ...ADMIN_SCHEMAS];

export function getSchemaBySlug(slug: string): ConfigSchema | undefined {
    return ALL_SCHEMAS.find((s) => s.$id === slug);
}
